import { useTranslation } from 'react-i18next';

import DhlModalWrapper from '@/components/common/DhlModalWrapperLogin';
import { DhlButton, DhlHeadline, DhlText } from '@/vendor/dhlReactInitial';

interface SessionTimeoutModalProps {
    open: boolean;
    secondsLeft: number;
    onContinue: () => void;
    onLogout: () => void;
}

/** Converts a total-seconds value to a "m:ss" string, e.g. 305 → "5:05". */
function formatMinutesSeconds(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * SessionTimeoutModal
 *
 * Warns the user that their session is about to expire due to inactivity.
 * Displays a live countdown and offers "Continue" or "Logout" actions.
 * Contains no business logic — all state and handlers come from props.
 */
function SessionTimeoutModal({
    open,
    secondsLeft,
    onContinue,
    onLogout,
}: SessionTimeoutModalProps) {
    const { t } = useTranslation();

    return (
        <DhlModalWrapper
            modalType="alert"
            isOpen={open}
            dataId="session-timeout-modal"
            trapFocus={true}
            handleOnClose={onContinue}
            shouldShowCloseButton
        >
            <DhlHeadline slot="header" designLevel="4" noMargin dataId="session-timeout-headline">
                {t('session.warning.title', 'Session About to Expire')}
            </DhlHeadline>

            <DhlText>
                {t(
                    'session.warning.body',
                    'Your session is about to expire due to inactivity. Do you want to continue?',
                )}
            </DhlText>

            <br />
            <DhlText>
                {t('session.warning.countdown', 'Time remaining: ${time}', {
                    time: formatMinutesSeconds(secondsLeft),
                })}
            </DhlText>

            <div slot="footer" className="tw-flex tw-w-full tw-justify-end tw-gap-2">
                <DhlButton variant="outline" onClick={onLogout}>
                    {t('session.warning.logout', 'Logout')}
                </DhlButton>
                <DhlButton onClick={onContinue}>
                    {t('session.warning.continue', 'Continue')}
                </DhlButton>
            </div>
        </DhlModalWrapper>
    );
}

export default SessionTimeoutModal;
